import {
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { IsActiveMatchOptions, NavigationEnd, Router } from '@angular/router';
import { Observable } from 'rxjs';

import { map } from 'rxjs/operators';
import { APP_USES_LOGIN } from 'src/environments/app-uses-login';
import { environment } from 'src/environments/environment';
import { AlertService } from './auth/services/alert.service';
import { AuthenticationService } from './auth/services/authentication.service';
import { ApplicationSettingsService } from './services/application-settings.service';
import { SubscriptionManager } from './_helpers/subscription-manager';
import { BrazeService } from './services/braze.service';
import { checkTrialAccountExpirationDate } from './_helpers/check-trial-account-expiration';
import { checkCreditCardStatus } from './_helpers/check-credit-card-expiration';
import { GoogleAnalytics } from './services/google-analytics.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ModalboxComponent } from './components/modalbox/modalbox.component';
import { SearchService } from './services/search.service';
import { DataVersionService } from './services/data-version.service';
import { Common } from '@trimblemaps/trimblemaps-js';
import { RouteService } from './services/route.service';
import { MapService } from './services/map.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  accept: boolean;
  showInModal$: Observable<boolean>;
  isFullScreenView$: Observable<boolean>;
  currentUser = this.authenticationService.currentUser;
  isAuthenticated = this.currentUser.pipe(
    map((user) => !this.appUsesLogin || user)
  );
  expiryModalShown: boolean;
  @HostListener('window:mousemove') refreshUserState() {
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    if (currentUser) {
      const currentDate = new Date();
      const userExpiryDate = new Date(currentUser.expiresAt);
      if (userExpiryDate >= currentDate) {
        const diff = Math.abs(userExpiryDate.getTime() - currentDate.getTime());
        const resultInMinutes = Math.round(diff / 60000);
        if (resultInMinutes <= 5) {
          if (!this.expiryModalShown) {
            this.showDialog(resultInMinutes);
            this.expiryModalShown = true;
          }
        }
      } else {
        //logout
        this.authenticationService.logout();
      }
    }
  }

  @HostListener('window:storage')
  onStorageChange() {
    const currentUser = localStorage.getItem('currentUser');
    this.subscriptionManager.manage = this.isAuthenticated.subscribe((val) => {
      if (!currentUser && val && !this.authenticationService.isPromoUser()) {
        //   other tab logged in will be logged out
        this.logout();
      }
    });
  }
  routerLinkActiveOptions: IsActiveMatchOptions = {
    matrixParams: 'ignored',
    queryParams: 'ignored',
    fragment: 'ignored',
    paths: 'exact',
  };
  private subscriptionManager = new SubscriptionManager();
  constructor(
    private brazeService: BrazeService,
    private router: Router,
    private appSettingsService: ApplicationSettingsService,
    private authenticationService: AuthenticationService,
    @Inject(APP_USES_LOGIN) public appUsesLogin: boolean,
    private alertService: AlertService,
    private googleAnalytics: GoogleAnalytics,
    private modalService: BsModalService,
    private searchService: SearchService,
    private dataVersionService: DataVersionService,
    private routeService: RouteService,
    private mapService: MapService
  ) {}

  ngOnInit() {
    this.brazeService.initBraze();
    this.subscriptionManager.manage =
      this.authenticationService.currentUser.subscribe((user) => {
        if (user) {
          if (user?.region.toLowerCase() !== Common.Region.EU) {
            this.dataVersionService.setDataVersionList();
          } else {
            this.routeService.logMinDataVersion('PCM_EU');
          }
          this.appSettingsService.retrieveAppSettingsDataSubscription();
          this.searchService.retrieveSingleSearchOptionDataSubscription();
        }
      });
    this.subscriptionManager.manage = this.router.events.subscribe(
      (routerEvent) => {
        if (routerEvent instanceof NavigationEnd) {
          this.accept = true;
          // Get your url
          if (routerEvent.url === '/terms' || routerEvent.url.match('/login')) {
            this.accept = false;
          }
          if (this.router.url.match('/?center')) {
            this.subscriptionManager.manage =
              this.authenticationService.currentUser.subscribe((user) => {
                if (this.authenticationService.isTrialUser(user)) {
                  const trialAccountExpiresOn =
                    checkTrialAccountExpirationDate(user);
                  const message = `TRIAL ACCOUNT Expires  ${trialAccountExpiresOn}. ${user.remainingRoutes}  Remaining routes `;
                  this.alertService.warning(
                    message,
                    true,
                    'app-modal-alert',
                    true
                  );
                }
                if (
                  checkCreditCardStatus(user)?.hasCCExpireWarn &&
                  user?.isMonthlyPaidCustomer
                ) {
                  const userWarnMsg =
                    // eslint-disable-next-line @typescript-eslint/quotes
                    " BILLING WARNING  The credit card that is registered with PC*MILER Web's Credit Card payment is scheduled to expire soon.";

                  this.alertService.warning(
                    userWarnMsg,
                    true,
                    'app-modal-alert',
                    true
                  );
                }
              });
          }
        }
      }
    );
    this.showInModal$ = this.alertService.showInModal$;
    this.isFullScreenView$ = this.mapService.fullScreenView$;
  }
  navigateToAccountPage() {
    this.router.navigate(['/accountSettings/myAccount']);
  }
  openSupportPage() {
    this.brazeService.track('Clicks Help');
    this.googleAnalytics.addGA(
      'help_support',
      'help_click',
      'help&support',
      'user clicked the help & support page'
    );
    window.open(environment.supportUrl, '_blank');
  }
  logout(): void {
    this.authenticationService.logout();
  }
  clearAlerts() {
    this.alertService.clear();
  }
  proceedToLogout() {
    this.brazeService.trackLogout();
    this.authenticationService.logout();
  }
  showDialog(minutes: number) {
    const initialState = {
      title: 'Session Expire Warning',
      list: [
        `Your session is going to expire in ${minutes} minute(s). please login to continue.`,
      ],
      actionButtonText: 'Go To Login',
      btnClass: 'btn btn-primary',
      okOnly: true,
    };
    const dialogRef = this.modalService.show(ModalboxComponent, {
      initialState,
      ignoreBackdropClick: true,
      keyboard: false,
      animated: true,
      class: 'modal-dialog-centered',
    });

    this.subscriptionManager.manage = dialogRef.content.event.subscribe(() => {
      this.proceedToLogout();
    });
  }

  ngOnDestroy(): void {
    this.subscriptionManager.unsubscribe();
  }
}
